import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Input, Select, Space, Modal, Form, Tag, Popconfirm,
  message, Card, Typography, Row, Col, Switch, Checkbox
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  LockOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { useActionColumn } from '../utils/tableLayout';

const { Title } = Typography;
const { Option } = Select;

function Users() {
  const actionColumn = useActionColumn(250);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    isActive: ''
  });
  
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [batchForm] = Form.useForm();
  const { user: currentUser, hasPermission } = useAuth();
  
  // 加载用户数据的函数
  useEffect(() => {
    let isCancelled = false;
    
    const loadData = async () => {
      if (loading) return;
      
      try {
        setLoading(true);
        const params = {
          page: pagination.current,
          limit: pagination.pageSize,
          search: filters.search || undefined,
          role: filters.role || undefined,
          isActive: filters.isActive || undefined
        };
        
        const response = await api.get('/users', { params });
        
        if (!isCancelled) {
          setUsers(response.data.users);
          setPagination(prev => ({
            ...prev,
            total: response.data.pagination.total
          }));
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('加载用户列表失败:', error);
          const errorMessage = error.response?.data?.message || '加载用户列表失败，请检查网络连接或重新登录';
          message.error(errorMessage);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };
    
    const timeoutId = setTimeout(loadData, 200); // 200ms 防抖
    
    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, filters.search, filters.role, filters.isActive]);

  // 手动重新加载数据的函数
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        search: filters.search || undefined,
        role: filters.role || undefined,
        isActive: filters.isActive || undefined
      };
      
      const response = await api.get('/users', { params });
      setUsers(response.data.users);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total
      }));
    } catch (error) {
      console.error('加载用户列表失败:', error);
      const errorMessage = error.response?.data?.message || '加载用户列表失败，请检查网络连接或重新登录';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, filters.search, filters.role, filters.isActive]);

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      message.success('删除成功');
      loadUsers();
    } catch (error) {
      console.error('删除失败:', error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingRecord) {
        await api.put(`/users/${editingRecord._id}`, values);
        message.success('更新成功');
      } else {
        await api.post('/auth/register', values);
        message.success('创建成功');
      }
      setModalVisible(false);
      loadUsers();
    } catch (error) {
      console.error('保存失败:', error);
      const errorMessage = error.response?.data?.message || '操作失败，请重试';
      message.error(errorMessage);
    }
  };

  const handleResetPassword = (record) => {
    setEditingRecord(record);
    passwordForm.resetFields();
    setPasswordModalVisible(true);
  };

  const handlePasswordSubmit = async (values) => {
    try {
      await api.post(`/users/${editingRecord._id}/reset-password`, values);
      message.success('密码重置成功');
      setPasswordModalVisible(false);
    } catch (error) {
      console.error('密码重置失败:', error);
    }
  };

  const handleBatchOperation = async (values) => {
    try {
      await api.post('/users/batch', {
        action: values.action,
        ids: selectedRowKeys,
        data: values.data
      });
      message.success('批量操作成功');
      setBatchModalVisible(false);
      setSelectedRowKeys([]);
      loadUsers();
    } catch (error) {
      console.error('批量操作失败:', error);
    }
  };

  const getRoleName = (role) => {
    const roleNames = {
      'super_admin': '超级管理员',
      'admin': '管理员',
      'user': '普通用户'
    };
    return roleNames[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      'super_admin': 'red',
      'admin': 'orange',
      'user': 'blue'
    };
    return colors[role] || 'default';
  };

  const formatPermissions = (permissions) => {
    const permissionLabels = {
      sites: '站点管理',
      pushRecords: '推送记录',
      groupConfig: '报名处理规则',
      robotSettings: '机器人设置',
      userManagement: '用户管理'
    };

    const activePermissions = [];
    Object.entries(permissions).forEach(([resource, actions]) => {
      const resourceLabel = permissionLabels[resource] || resource;
      const hasAnyPermission = Object.values(actions).some(Boolean);
      if (hasAnyPermission) {
        activePermissions.push(resourceLabel);
      }
    });

    return activePermissions.length > 0 ? activePermissions.join(', ') : '无权限';
  };

  const columns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 140,
      ellipsis: true
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      ellipsis: true
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 130,
      render: (role) => (
        <Tag color={getRoleColor(role)}>
          {getRoleName(role)}
        </Tag>
      )
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 90,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '正常' : '已禁用'}
        </Tag>
      )
    },
    {
      title: '权限',
      dataIndex: 'permissions',
      key: 'permissions',
      width: 150,
      ellipsis: true,
      render: (permissions, record) => {
        if (record.role === 'super_admin') {
          return <Tag color="red">所有权限</Tag>;
        }
        return <span style={{ fontSize: '12px' }}>{formatPermissions(permissions)}</span>;
      }
    },
    {
      title: '最后登录',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      width: 170,
      render: (date) => date ? new Date(date).toLocaleString('zh-CN') : '从未登录'
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (date) => new Date(date).toLocaleString('zh-CN')
    },
    {
      title: '操作',
      key: 'action',
      ...actionColumn,
      render: (_, record) => (
        <Space>
          {hasPermission('userManagement', 'write') && (
            <>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              >
                编辑
              </Button>
              <Button
                type="link"
                icon={<LockOutlined />}
                onClick={() => handleResetPassword(record)}
              >
                重置密码
              </Button>
            </>
          )}
          {hasPermission('userManagement', 'write') && record._id !== currentUser?._id && (
            <Popconfirm
              title="确定删除这个用户吗？"
              onConfirm={() => handleDelete(record._id)}
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled: record._id === currentUser?._id, // 不能选择自己
    }),
  };

  return (
    <div>
      <div className="page-header">
        <Title level={2}>用户管理</Title>
        <div className="description">管理系统用户和权限分配</div>
      </div>

      <Card>
        <div className="table-toolbar">
          <div className="left">
            <Input.Search
              placeholder="搜索用户名或邮箱"
              allowClear
              style={{ width: 300 }}
              onSearch={handleSearch}
            />
            <Select
              placeholder="选择角色"
              allowClear
              style={{ width: 150 }}
              value={filters.role || undefined}
              onChange={(value) => handleFilterChange('role', value)}
            >
              <Option value="super_admin">超级管理员</Option>
              <Option value="admin">管理员</Option>
              <Option value="user">普通用户</Option>
            </Select>
            <Select
              placeholder="选择状态"
              allowClear
              style={{ width: 120 }}
              value={filters.isActive || undefined}
              onChange={(value) => handleFilterChange('isActive', value)}
            >
              <Option value="true">正常</Option>
              <Option value="false">已禁用</Option>
            </Select>
          </div>
          <div className="right">
            {hasPermission('userManagement', 'write') && selectedRowKeys.length > 0 && (
              <Button
                onClick={() => setBatchModalVisible(true)}
              >
                批量操作 ({selectedRowKeys.length})
              </Button>
            )}
            {hasPermission('userManagement', 'write') && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                添加用户
              </Button>
            )}
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1332 }}
          tableLayout="fixed"
          rowSelection={hasPermission('userManagement', 'write') ? rowSelection : null}
        />
      </Card>

      {/* 添加/编辑用户模态框 */}
      <Modal
        title={editingRecord ? '编辑用户' : '添加用户'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label="用户名"
                rules={[
                  { required: true, message: '请输入用户名' },
                  { min: 3, max: 20, message: '用户名长度为3-20个字符' }
                ]}
              >
                <Input placeholder="请输入用户名" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="邮箱"
                rules={[
                  { required: true, message: '请输入邮箱' },
                  { type: 'email', message: '请输入有效的邮箱地址' }
                ]}
              >
                <Input placeholder="请输入邮箱" />
              </Form.Item>
            </Col>
          </Row>

          {!editingRecord && (
            <Form.Item
              name="password"
              label="密码"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码长度至少6个字符' }
              ]}
            >
              <Input.Password placeholder="请输入密码" />
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="role"
                label="角色"
                rules={[{ required: true, message: '请选择角色' }]}
              >
                <Select placeholder="请选择角色">
                  {currentUser.role === 'super_admin' && (
                    <Option value="super_admin">超级管理员</Option>
                  )}
                  {['super_admin', 'admin'].includes(currentUser.role) && (
                    <Option value="admin">管理员</Option>
                  )}
                  <Option value="user">普通用户</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isActive"
                label="账户状态"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch checkedChildren="正常" unCheckedChildren="禁用" />
              </Form.Item>
            </Col>
          </Row>

          {/* 权限设置 */}
          <Card title="权限设置" size="small" style={{ marginBottom: 16 }}>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.role !== currentValues.role
              }
            >
              {({ getFieldValue }) => {
                const role = getFieldValue('role');
                if (role === 'super_admin') {
                  return <div style={{ color: '#8c8c8c' }}>超级管理员拥有所有权限</div>;
                }
                
                return (
                  <div>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="站点管理">
                          <Form.Item name={['permissions', 'sites', 'read']} valuePropName="checked" noStyle>
                            <Checkbox>查看</Checkbox>
                          </Form.Item>
                          <Form.Item name={['permissions', 'sites', 'write']} valuePropName="checked" noStyle>
                            <Checkbox>编辑</Checkbox>
                          </Form.Item>
                          <Form.Item name={['permissions', 'sites', 'delete']} valuePropName="checked" noStyle>
                            <Checkbox>删除</Checkbox>
                          </Form.Item>
                          <Form.Item name={['permissions', 'sites', 'batch']} valuePropName="checked" noStyle>
                            <Checkbox>批量操作</Checkbox>
                          </Form.Item>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="推送记录">
                          <Form.Item name={['permissions', 'pushRecords', 'read']} valuePropName="checked" noStyle>
                            <Checkbox>查看</Checkbox>
                          </Form.Item>
                          <Form.Item name={['permissions', 'pushRecords', 'export']} valuePropName="checked" noStyle>
                            <Checkbox>导出</Checkbox>
                          </Form.Item>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="微信记录">
                          <Form.Item name={['permissions', 'wechatRecords', 'read']} valuePropName="checked" noStyle>
                            <Checkbox>查看</Checkbox>
                          </Form.Item>
                          <Form.Item name={['permissions', 'wechatRecords', 'write']} valuePropName="checked" noStyle>
                            <Checkbox>编辑</Checkbox>
                          </Form.Item>
                          <Form.Item name={['permissions', 'wechatRecords', 'delete']} valuePropName="checked" noStyle>
                            <Checkbox>删除</Checkbox>
                          </Form.Item>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="报名处理规则">
                          <Form.Item name={['permissions', 'groupConfig', 'read']} valuePropName="checked" noStyle>
                            <Checkbox>查看</Checkbox>
                          </Form.Item>
                          <Form.Item name={['permissions', 'groupConfig', 'write']} valuePropName="checked" noStyle>
                            <Checkbox>编辑</Checkbox>
                          </Form.Item>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="用户管理">
                          <Form.Item name={['permissions', 'userManagement', 'read']} valuePropName="checked" noStyle>
                            <Checkbox>查看</Checkbox>
                          </Form.Item>
                          <Form.Item name={['permissions', 'userManagement', 'write']} valuePropName="checked" noStyle>
                            <Checkbox>编辑</Checkbox>
                          </Form.Item>
                        </Form.Item>
                      </Col>
                    </Row>
                    {currentUser.role === 'super_admin' && (
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item label="机器人设置">
                            <Form.Item name={['permissions', 'robotSettings', 'read']} valuePropName="checked" noStyle>
                              <Checkbox>查看</Checkbox>
                            </Form.Item>
                            <Form.Item name={['permissions', 'robotSettings', 'write']} valuePropName="checked" noStyle>
                              <Checkbox>编辑</Checkbox>
                            </Form.Item>
                          </Form.Item>
                        </Col>
                      </Row>
                    )}
                  </div>
                );
              }}
            </Form.Item>
          </Card>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingRecord ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 重置密码模态框 */}
      <Modal
        title="重置密码"
        open={passwordModalVisible}
        onCancel={() => setPasswordModalVisible(false)}
        footer={null}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordSubmit}
        >
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码长度至少6个字符' }
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                重置密码
              </Button>
              <Button onClick={() => setPasswordModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量操作模态框 */}
      <Modal
        title="批量操作"
        open={batchModalVisible}
        onCancel={() => setBatchModalVisible(false)}
        footer={null}
      >
        <Form
          form={batchForm}
          layout="vertical"
          onFinish={handleBatchOperation}
        >
          <Form.Item
            name="action"
            label="操作类型"
            rules={[{ required: true, message: '请选择操作类型' }]}
          >
            <Select placeholder="请选择操作类型">
              <Option value="delete">批量删除</Option>
              <Option value="activate">批量激活</Option>
              <Option value="deactivate">批量禁用</Option>
              <Option value="updateRole">批量更新角色</Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.action !== currentValues.action
            }
          >
            {({ getFieldValue }) => {
              const action = getFieldValue('action');
              if (action === 'updateRole') {
                return (
                  <Form.Item
                    name={['data', 'role']}
                    label="新角色"
                    rules={[{ required: true, message: '请选择新角色' }]}
                  >
                    <Select placeholder="请选择新角色">
                      {currentUser.role === 'super_admin' && (
                        <Option value="super_admin">超级管理员</Option>
                      )}
                      {['super_admin', 'admin'].includes(currentUser.role) && (
                        <Option value="admin">管理员</Option>
                      )}
                      <Option value="user">普通用户</Option>
                    </Select>
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                确认操作
              </Button>
              <Button onClick={() => setBatchModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Users;
